import { topicPermissions } from "@/features/permissions";
import { CenteredLoading, CustomInputField } from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Checkbox,
  FormHelperText,
  List,
  ListItemButton,
  ListItemIcon,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./role-form-constants";

export const RoleForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const {
    control,
    register,
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
  const selectedTopic = topicPermissions[selectedTopicIndex];

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({
      ...defaultValues,
      permissions: Object.fromEntries(
        defaultValues.permissions.map(({ action }) => [action, true]),
      ),
    });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) =>
    await onSubmit({
      ...formData,
      permissions: Object.entries(formData.permissions).reduce(
        (permissions, [action, value]) =>
          value ? [...permissions, { action }] : permissions,
        [],
      ),
    });

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit(onFormSubmit)}
      spacing={3}
    >
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <CustomInputField
          required
          control={control}
          name="name"
          label="Nama"
          register={register}
          helperText={errors.name?.message}
          error={Boolean(errors.name)}
          InputProps={{
            placeholder: "Cth: Manager",
          }}
        />
      </Paper>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography
          color={errors.permissions ? "error.main" : "text.primary"}
          variant="h6"
        >
          Hak Akses
        </Typography>
        {errors.permissions && (
          <FormHelperText error>
            {errors.permissions.root.message}
          </FormHelperText>
        )}
        <Grid container spacing={2} sx={{
          mt: 2
        }}>
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{
              padding: 2,
              borderRadius: 2,

              border: {
                border: "1px solid #ddd",
              }
            }}>
            <List
              disablePadding
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {topicPermissions.map((topic, index) => (
                <ListItemButton
                  key={topic.name}
                  selected={index === selectedTopicIndex}
                  onClick={() => setSelectedTopicIndex(index)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon>{topic.icon}</ListItemIcon>
                  <Typography>{topic.name}</Typography>
                </ListItemButton>
              ))}
            </List>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <TableContainer sx={{ flex: 1 }}>
              <Table>
                <TableHead sx={{ backgroundColor: "#ececec" }}>
                  <TableRow>
                    <TableCell>Fitur</TableCell>
                    {selectedTopic.headers.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedTopic.features.map(
                    ({ name: featureName, permissions }) => (
                      <TableRow key={featureName}>
                        <TableCell>{featureName}</TableCell>
                        {permissions.map(({ action }, index) => (
                          <TableCell key={index} size="small">
                            {action ? (
                              <Checkbox
                                {...register(`permissions.${action}`)}
                                checked={
                                  watch(`permissions.${action}`) || false
                                }
                              />
                            ) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
      {renderActionButton({ isSubmitting })}
    </Stack>
  );
};

RoleForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  isLoading: PropTypes.bool,
};
